import React from "react"
import {
  Form, FormQuestion, QuestionTypes, SegmentRuleField,
} from "sharedTypes"
import Icon from "components/elements/Icon"
import GuestListIcon from "components/elements/GuestListIcon"
import WhatsAppIcon from "components/elements/WhatsAppIcon"
import { stripHtmlTags } from "utilities/strings"
import {
  FilterFieldConfig,
  FilterFieldGroupKey,
  FilterOperatorOption,
  FilterOptionsContext,
} from "./types"

// Form-question field prefix. Wire format: `"Form Question:<question.id>"`
export const FORM_QUESTION_FIELD_PREFIX = "Form Question:"

// Sentinel field key for the "Form Answers" category entry in FILTER_FIELDS.
export const FORM_QUESTION_SENTINEL_FIELD = "Form Question"

// Sentinel field key for the "Form Submission" category entry in FILTER_FIELDS.
// Selecting it opens a form-picker → Submitted / Not Submitted two-step flow.
export const FORM_SUBMISSION_SENTINEL_FIELD = "Form Submission"

export const formQuestionFieldKey = (questionId: string): string => (
  `${FORM_QUESTION_FIELD_PREFIX}${questionId}`
)

// Returns true when the question type produces a filter chip. TextBlock and
// FileUpload have no meaningful answer to filter on.
export const isFilterableQuestionType = (type: QuestionTypes): boolean => (
  type !== QuestionTypes.TextBlock && type !== QuestionTypes.FileUpload
)

// Operator presets per value-type. Backend accepts these strings verbatim
// (segment_viewer.rb OPERATOR_MAP). Order is display order in the dropdown.
const ENUM_OPERATORS: FilterOperatorOption[] = [
  { value: "eq", label: "is" },
  { value: "ne", label: "is not" },
]
// Multi-select enum: shows scalar labels for the 0–1 value case; the chip
// operator component auto-promotes to "is any of" / "is not" at ≥2 values.
const MULTI_ENUM_OPERATORS: FilterOperatorOption[] = [
  { value: "eq", label: "is" },
  { value: "ne", label: "is not" },
  { value: "in", label: "is any of" },
  { value: "not_in", label: "is not any of" },
]
const NUMBER_OPERATORS: FilterOperatorOption[] = [
  { value: "eq", label: "is" },
  { value: "ne", label: "is not" },
  { value: "gt", label: "greater than" },
  { value: "lt", label: "less than" },
]
const TEXT_OPERATORS: FilterOperatorOption[] = [
  { value: "contains", label: "contains" },
  { value: "not_contains", label: "does not contain" },
]
const DATE_OPERATORS: FilterOperatorOption[] = [
  { value: "eq", label: "is" },
  { value: "ne", label: "is not" },
  { value: "lt", label: "before" },
  { value: "gt", label: "after" },
]

const formOptions = ({ forms }: FilterOptionsContext) => (forms ?? []).map((f: Form) => ({
  label: f.name,
  value: f.id.toString(),
}))

// Field order matches the mockup. AddFilterPopover renders this array
// flat — the `group` annotation is kept on each entry for future grouping
// UI but doesn't drive picker ordering today.
export const FILTER_FIELDS: FilterFieldConfig[] = [
  {
    field: SegmentRuleField.GuestList,
    label: "Tags",
    icon: <Icon icon="tag" />,
    group: "Lists",
    type: "enum",
    operators: ENUM_OPERATORS,
    defaultOperator: "eq",
    multiSelect: true,
    multiValuePerGuest: true,
    deletedLabel: "Deleted tag",
    getOptions: ({ eventGroups }) => eventGroups.map(({
      id, title, icon, color,
    }) => ({
      label: title,
      value: id.toString(),
      // Picker passes boxSize=5; chip leaves it unset → 3 keeps chip
      // parity with the legacy IcoMoon size.
      renderIcon: ({ boxSize = 3 } = {}) => React.createElement(GuestListIcon, {
        icon, color, boxSize, flexShrink: 0,
      }),
    })),
  },
  {
    field: SegmentRuleField.Confirmation,
    label: "Confirmation Status",
    icon: <Icon icon="status-all" />,
    group: "Status",
    type: "enum",
    operators: ENUM_OPERATORS,
    defaultOperator: "eq",
    multiSelect: true,
    // Colour conventions match the sidebar segment-picker in
    // `ScrollView/Filters/index.tsx`. Keeps the chip visuals consistent
    // with the rest of the guest UI.
    getOptions: () => [
      {
        label: "Confirmed", value: "Confirmed", icon: "dot", iconColor: "blue.300",
      },
      {
        label: "Unconfirmed", value: "Unconfirmed", icon: "dot", iconColor: "gray.400",
      },
      {
        label: "Declined", value: "Declined", icon: "dot", iconColor: "red.300",
      },
      {
        label: "Waitlisted", value: "Waitlisted", icon: "dot", iconColor: "yellow.300",
      },
    ],
  },
  {
    field: SegmentRuleField.Email,
    label: "Email Validity",
    icon: <Icon icon="emails" />,
    group: "Status",
    type: "enum",
    operators: ENUM_OPERATORS,
    defaultOperator: "eq",
    multiSelect: true,
    getOptions: () => [
      { label: "Valid", value: "Valid" },
      { label: "Blank", value: "Blank" },
      { label: "Invalid", value: "Invalid" },
      { label: "Unsubscribed", value: "Unsubscribed" },
      { label: "Hard Bounced", value: "Hard Bounced" },
      { label: "Soft Bounced", value: "Soft Bounced" },
    ],
  },
  {
    field: SegmentRuleField.EmailCampaign,
    label: "Email Campaigns",
    icon: <Icon icon="emails" />,
    group: "Activity",
    type: "enum",
    operators: ENUM_OPERATORS,
    defaultOperator: "eq",
    multiSelect: true,
    multiValuePerGuest: true,
    deletedLabel: "Deleted campaign",
    getOptions: ({ emails }) => emails.map(({ id, title }) => ({
      label: title,
      value: id.toString(),
    })),
  },
  {
    field: SegmentRuleField.EmailsSent,
    label: "Emails Sent",
    icon: <Icon icon="emails" />,
    group: "Activity",
    type: "number",
    operators: NUMBER_OPERATORS,
    defaultOperator: "eq",
  },
  {
    field: SegmentRuleField.PlusOnesCount,
    label: "Plus-Ones Count",
    icon: <Icon icon="settings-team1" />,
    group: "Status",
    type: "number",
    operators: NUMBER_OPERATORS,
    defaultOperator: "eq",
  },
  {
    field: SegmentRuleField.CreatedDate,
    label: "Created Date",
    icon: <Icon icon="dash-calendar" />,
    group: "Activity",
    type: "date",
    operators: DATE_OPERATORS,
    defaultOperator: "eq",
  },
  {
    field: SegmentRuleField.LastConfirmedDate,
    label: "Last Confirmed Date",
    icon: <Icon icon="dash-calendar" />,
    group: "Activity",
    type: "date",
    operators: DATE_OPERATORS,
    defaultOperator: "eq",
  },
  {
    field: SegmentRuleField.CheckInDate,
    label: "Check-In Date",
    icon: <Icon icon="dash-calendar" />,
    group: "Activity",
    type: "date",
    operators: DATE_OPERATORS,
    defaultOperator: "eq",
  },
  {
    field: SegmentRuleField.CheckInStatus,
    label: "Check-In Status",
    icon: <Icon icon="checkin-filled" />,
    group: "Status",
    type: "enum",
    operators: ENUM_OPERATORS,
    defaultOperator: "eq",
    multiSelect: true,
    getOptions: () => [
      {
        label: "Checked In", value: "Arrived", icon: "dot", iconColor: "green.500",
      },
      {
        label: "Not Checked In", value: "Unarrived", icon: "dot", iconColor: "orange.300",
      },
    ],
  },
  {
    field: SegmentRuleField.Seating,
    label: "Seating Status",
    icon: <Icon icon="seating" />,
    group: "Status",
    type: "enum",
    operators: ENUM_OPERATORS,
    defaultOperator: "eq",
    multiSelect: true,
    getOptions: () => [
      { label: "Assigned", value: "Assigned" },
      { label: "Standing", value: "Standing" },
    ],
  },
  {
    field: SegmentRuleField.EmailSubscription,
    label: "Email Subscription",
    icon: <Icon icon="email-only-large" />,
    group: "Status",
    type: "enum",
    operators: ENUM_OPERATORS,
    defaultOperator: "eq",
    multiSelect: false,
    getOptions: () => [
      { label: "Subscribed", value: "Subscribed" },
      { label: "Unsubscribed", value: "Unsubscribed" },
    ],
  },
  {
    field: SegmentRuleField.WhatsAppOptIn,
    label: "WhatsApp Opt-In",
    icon: <WhatsAppIcon size={4} color="currentColor" />,
    group: "Status",
    type: "enum",
    operators: ENUM_OPERATORS,
    defaultOperator: "eq",
    multiSelect: false,
    featureFlag: "whatsappMessaging",
    getOptions: () => [
      { label: "Opted In", value: "Opted In" },
      { label: "Not Opted In", value: "Not Opted In" },
    ],
  },
  {
    field: SegmentRuleField.Company,
    label: "Company",
    icon: <Icon icon="settings-team1" />,
    group: "Status",
    type: "enum",
    operators: MULTI_ENUM_OPERATORS,
    defaultOperator: "eq",
    multiSelect: true,
    deletedLabel: "Unknown company",
    getOptions: ({ distinctPersonValues }) => (
      (distinctPersonValues?.company ?? []).map((name) => ({ label: name, value: name }))
    ),
  },
  {
    field: SegmentRuleField.JobTitle,
    label: "Job Title",
    icon: <Icon icon="settings-team1" />,
    group: "Status",
    type: "enum",
    operators: MULTI_ENUM_OPERATORS,
    defaultOperator: "eq",
    multiSelect: true,
    deletedLabel: "Unknown job title",
    getOptions: ({ distinctPersonValues }) => (
      (distinctPersonValues?.jobTitle ?? []).map((title) => ({ label: title, value: title }))
    ),
  },
  // Sentinel: opens form-picker → Submitted / Not Submitted two-step flow.
  // The resulting chip uses FormSubmitted or FormNotSubmitted as the field key.
  {
    field: FORM_SUBMISSION_SENTINEL_FIELD,
    label: "Form Submission",
    icon: <Icon icon="form" />,
    group: "Form Answers",
    type: "enum",
    operators: [],
    defaultOperator: "eq",
    isFormSubmissionCategory: true,
  },
  // Sentinel: selecting this in step 1 opens the question sub-picker (step 2)
  // rather than jumping to a value editor. Per-question config is built at
  // runtime via `buildFormQuestionConfig` once a question is chosen.
  {
    field: FORM_QUESTION_SENTINEL_FIELD,
    label: "Form Answers",
    icon: <Icon icon="form" />,
    group: "Form Answers",
    // type/operators/defaultOperator are placeholders — the picker intercepts
    // this entry before reaching the value step, so these are never used.
    type: "enum",
    operators: [],
    defaultOperator: "eq",
    isFormQuestionCategory: true,
  },
  // Hidden from the picker — only used to render existing chips from saved
  // segments or URL rules that reference these field keys directly.
  {
    field: SegmentRuleField.FormSubmitted,
    label: "Form Submitted",
    icon: <Icon icon="form" />,
    group: "Form Answers",
    type: "enum",
    operators: MULTI_ENUM_OPERATORS,
    defaultOperator: "eq",
    multiSelect: true,
    deletedLabel: "Deleted form",
    hiddenFromPicker: true,
    getOptions: formOptions,
  },
  {
    field: SegmentRuleField.FormNotSubmitted,
    label: "Form Not Submitted",
    icon: <Icon icon="form" />,
    group: "Form Answers",
    type: "enum",
    operators: MULTI_ENUM_OPERATORS,
    defaultOperator: "eq",
    multiSelect: true,
    deletedLabel: "Deleted form",
    hiddenFromPicker: true,
    getOptions: formOptions,
  },
]

// Cross-group display order in the AddFilterPopover picker.
export const FILTER_FIELD_GROUP_ORDER: FilterFieldGroupKey[] = [
  "Status", "Activity", "Lists", "Form Answers",
]

const FIELD_CONFIG_MAP: Map<string, FilterFieldConfig> = new Map(
  FILTER_FIELDS.map((config) => [config.field, config]),
)

export const getFieldConfig = (field: string): FilterFieldConfig | undefined => (
  FIELD_CONFIG_MAP.get(field)
)

// ─── Form-question dynamic configs ────────────────────────────────────────────

/**
 * Derives a `FilterFieldConfig` for a single form question. Used by:
 *   - `AddFilterPopover` (step 3) to determine the value editor for a
 *     just-selected question.
 *   - `buildFormQuestionConfigs` (used by `resolveFieldConfig`) to build the
 *     full set for rendering existing chips from URL/segment rules.
 */
export const buildFormQuestionConfig = (question: FormQuestion): FilterFieldConfig => {
  // question.id IS the stable external_id from the API serializer.
  const field = formQuestionFieldKey(question.id)

  switch (question.type) {
    case QuestionTypes.SingleAnswer:
    case QuestionTypes.MultipleAnswer:
    case QuestionTypes.DropdownMenu:
      return {
        field,
        label: stripHtmlTags(question.title),
        icon: <Icon icon="form" />,
        group: "Form Answers",
        type: "enum",
        operators: MULTI_ENUM_OPERATORS,
        defaultOperator: "eq",
        multiSelect: true,
        getOptions: () => question.choices.map((c) => ({ label: c, value: c })),
      }
    case QuestionTypes.Terms:
      // Terms is a single checkbox stored the same way as a one-choice
      // Multiple Answer question: the recorded answer IS choices[0]
      // (the consent statement text), not a synthesized "Accepted" value.
      return {
        field,
        label: stripHtmlTags(question.title),
        icon: <Icon icon="form" />,
        group: "Form Answers",
        type: "enum",
        operators: ENUM_OPERATORS,
        defaultOperator: "eq",
        multiSelect: false,
        // Legacy questions saved before consent text was required can have
        // an empty choices array — no option to offer in that case.
        getOptions: () => (
          question.choices[0] ? [{ label: question.choices[0], value: question.choices[0] }] : []
        ),
      }
    case QuestionTypes.SingleLineText:
    case QuestionTypes.ParagraphText:
    default:
      // Free-text answers aren't enumerable — an open-ended question like
      // "Feedback" can have as many distinct values as there are guests.
      // contains / does not contain against the raw text is the only
      // sensible filter here.
      return {
        field,
        label: stripHtmlTags(question.title),
        icon: <Icon icon="form" />,
        group: "Form Answers",
        type: "text",
        operators: TEXT_OPERATORS,
        defaultOperator: "contains",
      }
  }
}

/**
 * Returns a `FilterFieldConfig` for every filterable form question.
 * TextBlock and FileUpload are excluded. Used by `resolveFieldConfig` to
 * find the config for an existing form-question chip (from URL or segment rules).
 */
export const buildFormQuestionConfigs = (questions: FormQuestion[]): FilterFieldConfig[] => (
  questions
    .filter((q) => isFilterableQuestionType(q.type))
    .map(buildFormQuestionConfig)
)

/**
 * Resolves a field key to its config, checking static fields first, then
 * the supplied dynamic form-question configs. Returns undefined for unknown
 * fields (stale URL, hand-edited, etc.).
 */
export const resolveFieldConfig = (
  field: string,
  formQuestionConfigs: FilterFieldConfig[],
): FilterFieldConfig | undefined => {
  const staticConfig = FIELD_CONFIG_MAP.get(field)
  if (staticConfig) return staticConfig

  return formQuestionConfigs.find((cfg) => cfg.field === field)
}

export const computeDisabledFields = (
  fields: FilterFieldConfig[],
  context: FilterOptionsContext,
): Set<string> => {
  const set = new Set<string>()
  fields.forEach((cfg) => {
    if (cfg.isFormQuestionCategory) {
      if (!context.formQuestions?.length) set.add(cfg.field)

      return
    }
    if (cfg.isFormSubmissionCategory) {
      if (!context.forms?.length) set.add(cfg.field)

      return
    }
    if (cfg.getOptions && cfg.getOptions(context).length === 0) {
      set.add(cfg.field)
    }
  })

  return set
}
